import type {
  CreateGiftRequest,
  CreateGiftResponse,
  GiftsListResponse,
  GiftByCodeResponse,
  ActivateGiftResponse,
  PicturesListResponse,
} from "../types/gifts";

const BASE_URL = "https://dpscontrol.ru/api/v1/user-gifts";

const getInitData = (): string => {
  return window.Telegram?.WebApp?.initData || import.meta.env.VITE_DEVELOP_MODE;
};

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = options.method || "GET";
  const url = endpoint.startsWith("http") ? endpoint : `${BASE_URL}${endpoint}`;
  const initData = getInitData();

  console.log(`🎁 [GIFTS API ${method}] ${endpoint}`, {
    url,
    body: options.body ? JSON.parse(options.body as string) : undefined,
    hasInitData: !!initData,
    initDataLength: initData?.length || 0,
  });

  const headers = {
    "Content-Type": "application/json",
    "X-Telegram-Init-Data": initData,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    console.error(`❌ [GIFTS API ${method}] ${endpoint} FAILED:`, {
      status: response.status,
      statusText: response.statusText,
      contentType,
    });

    // Если вернулся HTML вместо JSON
    if (contentType?.includes("text/html")) {
      throw new Error(`Server returned HTML instead of JSON (HTTP ${response.status}). Endpoint may not exist.`);
    }

    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) {
    console.error(`❌ [GIFTS API ${method}] ${endpoint} received non-JSON response:`, {
      contentType,
      url,
    });
    throw new Error(`Server returned ${contentType} instead of JSON. Endpoint may not exist.`);
  }

  const data = await response.json();
  console.log(`✅ [GIFTS API ${method}] ${endpoint} SUCCESS:`, data);
  return data;
}

export const giftsAPI = {
  /**
   * GET /api/v1/user-gifts - Получить список моих подарков
   */
  async getMyGifts(): Promise<GiftsListResponse> {
    return fetchAPI<GiftsListResponse>("");
  },

  /**
   * GET /api/v1/user-gifts?code=ABC123 - Получить информацию о подарке по коду
   */
  async getGiftByCode(code: string): Promise<GiftByCodeResponse> {
    return fetchAPI<GiftByCodeResponse>(`?code=${code}`);
  },

  /**
   * POST /api/v1/user-gifts - Создать новый подарок
   */
  async createGift(data: CreateGiftRequest): Promise<CreateGiftResponse> {
    return fetchAPI<CreateGiftResponse>("", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * POST /api/v1/user-gifts?code=ABC123 - Активировать подарок
   */
  async activateGift(code: string): Promise<ActivateGiftResponse> {
    return fetchAPI<ActivateGiftResponse>(`?code=${code}`, {
      method: "POST",
    });
  },

  /**
   * GET /api/v1/user-gifts/pictures - Получить список картинок
   */
  async getPictures(): Promise<PicturesListResponse> {
    return fetchAPI<PicturesListResponse>("/pictures");
  },
};
